import React, { useRef, useState } from 'react';
import { entitiesApi } from '../../api/entities';
import { pendingOffersApi } from '../../api/pendingOffers';
import { offerKindLabel } from '../../utils/chatIdeaOffers';
import {
  ADD_TO_BOOK_NOTES,
  CAST_NOT_NOW,
  DRAFT_SAVED,
  IDEA_OFFER_DISMISS_FAILED,
  IDEA_OFFER_SAVE_FAILED,
} from '../../utils/writerCopy';
import './ChatIdeaOffers.css';

/**
 * One card per idea, open under the reply. Each idea is a button pair:
 * save it as a Note, or dismiss it. Not words inside the reply.
 */
export default function ChatIdeaOffers({
  offers,
  storyId,
  onResolved,
  trailing = null,
  acceptOffer = (id, body) => pendingOffersApi.accept(id, body),
  entityApi = entitiesApi,
}) {
  const [busyId, setBusyId] = useState('');
  const [savedIds, setSavedIds] = useState([]);
  const [error, setError] = useState('');
  const busyRef = useRef('');
  const list = offers || [];
  if (!list.length) return trailing || null;

  const save = async (offer) => {
    const description = String(offer.quoted_text || offer.name || '').trim();
    const id = String(offer.id || '');
    if (!storyId || !description || !id) {
      setError(IDEA_OFFER_SAVE_FAILED);
      return;
    }
    if (busyRef.current) return;
    busyRef.current = id;
    setBusyId(id);
    setError('');
    let ok = false;
    try {
      await acceptOffer(storyId, { kind: 'chat', id: offer.id });
      ok = true;
    } catch (err) {
      setError(IDEA_OFFER_SAVE_FAILED);
    } finally {
      busyRef.current = '';
      setBusyId('');
    }
    if (!ok) return;
    setSavedIds((prev) => (prev.includes(id) ? prev : prev.concat(id)));
    onResolved?.(offer.id);
  };

  const dismiss = async (offer) => {
    const id = String(offer.id || '');
    if (busyRef.current) return;
    busyRef.current = id;
    setBusyId(id);
    setError('');
    try {
      await entityApi.skip(offer.id);
      onResolved?.(offer.id);
    } catch (err) {
      setError(IDEA_OFFER_DISMISS_FAILED);
    } finally {
      busyRef.current = '';
      setBusyId('');
    }
  };

  return (
    <div className="idea-offers" data-testid="idea-offers">
      <ul className="idea-offers__list">
        {list.map((offer) => {
          const busy = busyId === String(offer.id);
          const saved = Boolean(offer.offer_saved) || savedIds.includes(String(offer.id));
          return (
            <li key={offer.id} className="idea-offers__item" data-testid="idea-offer">
              <span className="idea-offers__name">{offer.name}</span>
              <span className="idea-offers__kind">{offerKindLabel(offer.entity_type)}</span>
              {saved ? (
                <span className="idea-offers__saved">{DRAFT_SAVED}</span>
              ) : (
              <>
              <button
                type="button"
                className="idea-offers__save"
                disabled={busy}
                onClick={() => { void save(offer); }}
              >
                {ADD_TO_BOOK_NOTES}
              </button>
              <button
                type="button"
                className="idea-offers__dismiss"
                disabled={busy}
                onClick={() => { void dismiss(offer); }}
              >
                {CAST_NOT_NOW}
              </button>
              </>
              )}
            </li>
          );
        })}
        {trailing ? <li className="idea-offers__extra">{trailing}</li> : null}
      </ul>
      {error ? <p className="idea-offers__error" role="alert">{error}</p> : null}
    </div>
  );
}
